// File: frontend/src/components/admin/KatifiMemoryPanel.js
// Description: Admin Brain Center tab — Katifi's persistent memory + Claude usage stats.
//   Three sections:
//     1. Claude API usage stats (window selector + by-model breakdown + recent calls)
//     2. Long-term memories table (filter by type, deactivate row)
//     3. Self-learner daily logs (last N days + manual trigger button)
//
// Backend endpoints (all under /api/v1/admin/katifi):
//   GET    /memories?memory_type=&limit=
//   DELETE /memories/{id}
//   GET    /learning-logs?limit=
//   POST   /learning/trigger
//   GET    /claude-usage?hours=&limit=
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/apiService';

const MEMORY_TYPES = [
    'all',
    'creator_preference',
    'skill_gap',
    'pattern',
    'fact',
    'correction',
    'style_note',
    'tool_idea',
];

const TYPE_COLORS = {
    creator_preference: '#a855f7',
    skill_gap:          '#ef4444',
    pattern:            '#3b82f6',
    fact:               '#10b981',
    correction:         '#f59e0b',
    style_note:         '#ec4899',
    tool_idea:          '#14b8a6',
};

const KatifiMemoryPanel = ({ activeTab }) => {
    const [memories, setMemories] = useState([]);
    const [logs, setLogs] = useState([]);
    const [usage, setUsage] = useState(null);
    const [usageWindow, setUsageWindow] = useState(24); // hours
    const [memoryFilter, setMemoryFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);

    useEffect(() => {
        if (activeTab === 'memory') {
            refreshAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, memoryFilter, usageWindow]);

    const refreshAll = async () => {
        setLoading(true);
        try {
            await Promise.all([fetchMemories(), fetchLogs(), fetchUsage()]);
        } catch (e) {
            console.error('[KatifiMemoryPanel] refresh failed', e);
        } finally {
            setLoading(false);
        }
    };

    const fetchMemories = async () => {
        const qs = memoryFilter !== 'all' ? `?memory_type=${memoryFilter}&limit=100` : '?limit=100';
        try {
            const data = await apiService.makeRequest(`/admin/katifi/memories${qs}`);
            setMemories(data.memories || []);
        } catch (e) {
            console.error('memories fetch failed', e);
            toast.error('Не удалось загрузить памяти');
        }
    };

    const fetchLogs = async () => {
        try {
            const data = await apiService.makeRequest('/admin/katifi/learning-logs?limit=14');
            setLogs(data.logs || []);
        } catch (e) {
            console.error('logs fetch failed', e);
        }
    };

    const fetchUsage = async () => {
        try {
            const data = await apiService.makeRequest(`/admin/katifi/claude-usage?hours=${usageWindow}&limit=20`);
            setUsage(data);
        } catch (e) {
            console.error('usage fetch failed', e);
            setUsage(null);
        }
    };

    const deactivateMemory = async (id, contentPreview) => {
        if (!window.confirm(`Деактивировать память?\n\n"${contentPreview.slice(0, 80)}..."`)) return;
        try {
            await apiService.makeRequest(`/admin/katifi/memories/${id}`, { method: 'DELETE' });
            toast.success('Память деактивирована');
            fetchMemories();
        } catch (e) {
            toast.error('Не удалось деактивировать');
        }
    };

    const triggerLearningCycle = async () => {
        if (triggering) return;
        setTriggering(true);
        try {
            const r = await apiService.makeRequest('/admin/katifi/learning/trigger', { method: 'POST' });
            toast.success(r.message || 'Cycle triggered');
        } catch (e) {
            toast.error('Не удалось запустить cycle');
        } finally {
            setTriggering(false);
        }
    };

    const fmtCost = (n) => n == null ? '$0' : (n < 0.01 ? `$${n.toFixed(6)}` : `$${n.toFixed(4)}`);
    const fmtDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' });
    };

    if (activeTab !== 'memory') return null;

    return (
        <div style={{ padding: '20px', maxWidth: 1400, margin: '0 auto' }}>
            <h2 style={{ marginBottom: 8, color: '#1e293b' }}>🧠 Katifi Memory Panel</h2>
            <p style={{ color: '#64748b', marginBottom: 24 }}>
                Long-term memories, Claude API spend, и журналы ежедневного self-learning.
            </p>

            {/* ── Claude usage stats ───────────────────────────── */}
            <section style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={h3Style}>Claude API Usage</h3>
                    <div>
                        {[24, 72, 168, 720].map(h => (
                            <button
                                key={h}
                                onClick={() => setUsageWindow(h)}
                                style={{
                                    ...pillButton,
                                    background: usageWindow === h ? '#6366f1' : '#e2e8f0',
                                    color: usageWindow === h ? '#fff' : '#475569',
                                }}
                            >
                                {h === 24 ? '24h' : h === 72 ? '3d' : h === 168 ? '7d' : '30d'}
                            </button>
                        ))}
                    </div>
                </div>

                {usage ? (
                    <>
                        <div style={statsGrid}>
                            <StatCard label="Calls" value={usage.window.calls} />
                            <StatCard label="Cost" value={fmtCost(usage.window.cost_usd)} />
                            <StatCard label="Tokens in" value={usage.window.tokens_in.toLocaleString()} />
                            <StatCard label="Tokens out" value={usage.window.tokens_out.toLocaleString()} />
                            <StatCard
                                label="Errors"
                                value={usage.window.errors}
                                accent={usage.window.errors > 0 ? '#ef4444' : '#10b981'}
                            />
                            <StatCard
                                label="Lifetime cost"
                                value={fmtCost(usage.lifetime.cost_usd)}
                                accent="#64748b"
                            />
                        </div>

                        {usage.by_model.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                                <strong style={{ color: '#475569', fontSize: 13 }}>By model:</strong>
                                <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                                    {usage.by_model.map(m => (
                                        <span key={m.model} style={modelPill}>
                                            <code>{m.model}</code> — {m.calls} calls, {fmtCost(m.cost_usd)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {usage.recent.length > 0 && (
                            <details style={{ marginTop: 16 }}>
                                <summary style={{ cursor: 'pointer', color: '#475569', fontSize: 13 }}>
                                    Recent {usage.recent.length} calls (click to expand)
                                </summary>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th>Time</th><th>Tenant</th><th>Model</th><th>Depth</th>
                                            <th>In/Cached/Out</th><th>Cost</th><th>Latency</th><th>Error</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usage.recent.map(r => (
                                            <tr key={r.id}>
                                                <td>{fmtDate(r.ts)}</td>
                                                <td>{r.tenant}</td>
                                                <td><code style={{ fontSize: 11 }}>{r.model}</code></td>
                                                <td>{r.depth}</td>
                                                <td>{r.tokens_in}/{r.cached_in}/{r.tokens_out}</td>
                                                <td>{fmtCost(r.cost_usd)}</td>
                                                <td>{r.latency_ms ? `${r.latency_ms}ms` : '—'}</td>
                                                <td style={{ color: r.error ? '#ef4444' : '#94a3b8' }}>
                                                    {r.error || 'ok'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </details>
                        )}
                    </>
                ) : (
                    <p style={{ color: '#94a3b8' }}>{loading ? 'Загрузка...' : 'Нет данных (миграция applied?)'}</p>
                )}
            </section>

            {/* ── Long-term memories ───────────────────────────── */}
            <section style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={h3Style}>Long-Term Memories ({memories.length})</h3>
                    <select
                        value={memoryFilter}
                        onChange={(e) => setMemoryFilter(e.target.value)}
                        style={selectStyle}
                    >
                        {MEMORY_TYPES.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>

                {memories.length === 0 ? (
                    <p style={{ color: '#94a3b8' }}>{loading ? 'Загрузка...' : 'Нет активных воспоминаний.'}</p>
                ) : (
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={{ width: 140 }}>Type</th>
                                <th>Content</th>
                                <th style={{ width: 140 }}>Source</th>
                                <th style={{ width: 70 }}>Conf</th>
                                <th style={{ width: 60 }}>Uses</th>
                                <th style={{ width: 130 }}>Created</th>
                                <th style={{ width: 50 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {memories.map(m => (
                                <tr key={m.id}>
                                    <td>
                                        <span style={{
                                            background: TYPE_COLORS[m.memory_type] || '#94a3b8',
                                            color: '#fff', padding: '2px 8px', borderRadius: 12,
                                            fontSize: 11, fontWeight: 600,
                                        }}>
                                            {m.memory_type}
                                        </span>
                                    </td>
                                    <td style={{ fontSize: 13, lineHeight: 1.4 }}>{m.content}</td>
                                    <td style={{ fontSize: 11, color: '#64748b' }}>{m.source || '—'}</td>
                                    <td>{m.confidence}</td>
                                    <td>{m.uses_count}</td>
                                    <td style={{ fontSize: 11, color: '#64748b' }}>{fmtDate(m.created_at)}</td>
                                    <td>
                                        <button
                                            onClick={() => deactivateMemory(m.id, m.content)}
                                            title="Деактивировать"
                                            style={deleteBtn}
                                        >✕</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* ── Self-learner daily logs ──────────────────────── */}
            <section style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={h3Style}>Self-Learner — Last {logs.length} cycles</h3>
                    <button
                        onClick={triggerLearningCycle}
                        disabled={triggering}
                        style={{
                            ...pillButton,
                            background: triggering ? '#94a3b8' : '#6366f1',
                            color: '#fff',
                            padding: '6px 14px',
                            cursor: triggering ? 'wait' : 'pointer',
                        }}
                    >
                        {triggering ? 'Triggering...' : '⚡ Trigger cycle now'}
                    </button>
                </div>

                {logs.length === 0 ? (
                    <p style={{ color: '#94a3b8' }}>
                        {loading ? 'Загрузка...' : 'Нет учебных циклов ещё. Beat fires daily at 03:30 UTC.'}
                    </p>
                ) : (
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Conversations</th>
                                <th>Memories +</th>
                                <th>Directives +</th>
                                <th>Quality (before → after)</th>
                                <th>Obsidian path</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(l => {
                                const delta = (l.quality_after || 0) - (l.quality_before || 0);
                                const deltaColor = delta > 0 ? '#10b981' : delta < 0 ? '#ef4444' : '#94a3b8';
                                return (
                                    <tr key={l.id}>
                                        <td><strong>{l.date}</strong></td>
                                        <td>{l.conversations_analyzed}</td>
                                        <td>{l.memories_added}</td>
                                        <td>{l.directives_updated}</td>
                                        <td>
                                            {l.quality_before ?? '?'} → {l.quality_after ?? '?'}
                                            {' '}
                                            <span style={{ color: deltaColor, fontWeight: 600 }}>
                                                ({delta > 0 ? '+' : ''}{delta})
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 11, color: '#64748b' }}>{l.obsidian_path || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
};

const StatCard = ({ label, value, accent = '#6366f1' }) => (
    <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        padding: 14, display: 'flex', flexDirection: 'column', gap: 4,
    }}>
        <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.5 }}>
            {label}
        </span>
        <span style={{ fontSize: 22, fontWeight: 700, color: accent }}>{value}</span>
    </div>
);

// ─── Inline styles ─────────────────────────────────────
const sectionStyle = {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
};
const h3Style = {
    margin: 0,
    color: '#1e293b',
    fontSize: 17,
    fontWeight: 600,
};
const statsGrid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12,
};
const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    marginTop: 12,
};
const pillButton = {
    padding: '4px 12px',
    border: 'none',
    borderRadius: 16,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: 6,
};
const modelPill = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    padding: '4px 10px',
    borderRadius: 14,
    fontSize: 12,
    color: '#475569',
};
const selectStyle = {
    padding: '6px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize: 13,
    background: '#fff',
};
const deleteBtn = {
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: 4,
    width: 26,
    height: 26,
    cursor: 'pointer',
    color: '#94a3b8',
    fontSize: 14,
};

// Inject a tiny stylesheet for table cells once
if (typeof document !== 'undefined' && !document.getElementById('katifi-memory-panel-css')) {
    const s = document.createElement('style');
    s.id = 'katifi-memory-panel-css';
    s.textContent = `
        .katifi-mem-table th { text-align: left; font-size: 11px; text-transform: uppercase;
            color: #64748b; letter-spacing: 0.5px; padding: 8px 6px; border-bottom: 1px solid #e2e8f0; }
        .katifi-mem-table td { padding: 8px 6px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
    `;
    document.head.appendChild(s);
}

export default KatifiMemoryPanel;
